import { useState } from "react";
import { Bell, Check, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface NotifyMeButtonProps {
  productId: number;
  productName: string;
}

const NotifyMeButton = ({ productId, productName }: NotifyMeButtonProps) => {
  const [email, setEmail] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    const { error } = await supabase
      .from("back_in_stock_notifications")
      .insert({ email: email.trim().toLowerCase(), product_id: productId });

    setLoading(false);

    if (error) {
      if (error.code === "23505") {
        toast.info("You're already on the list! We'll email you when it's back.");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
      return;
    }

    setSubmitted(true);
    toast.success(`We'll notify you when ${productName} is back in stock!`);
  };

  if (submitted) {
    return (
      <div className="flex items-center gap-2 bg-primary/10 text-primary rounded-full px-5 py-3 font-body text-sm">
        <Check className="w-4 h-4" />
        <span>You'll be notified when it's back!</span>
      </div>
    );
  }

  if (!expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground font-body text-sm font-semibold uppercase tracking-wider py-3.5 rounded-full hover:bg-primary/90 transition-colors"
      >
        <Bell className="w-4 h-4" />
        Notify Me When Back in Stock
      </button>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-2">
      <p className="text-xs font-body text-muted-foreground">
        Enter your email and we'll let you know when <strong>{productName}</strong> is back in stock.
      </p>
      <div className="flex gap-2">
        <input
          type="email"
          required
          placeholder="your@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="flex-1 h-11 rounded-full border border-input bg-background px-4 text-sm font-body placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
        <button
          type="submit"
          disabled={loading}
          className="h-11 px-5 rounded-full bg-primary text-primary-foreground font-body text-sm font-semibold uppercase tracking-wider hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center gap-2 shrink-0"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
          Notify Me
        </button>
      </div>
    </form>
  );
};

export default NotifyMeButton;
